// @ts-check
/* eslint-env browser */
import introJs from 'intro.js'
import { TOUR_STEPS_MAP, STEP_ACTIONS_MAP, IS_STEP_AVAILABLE_MAP, TOUR_CONTAINER_MAP } from './onboarding-config'

const START_ICON_SVG = '<svg class="govuk-button__start-icon" xmlns="http://www.w3.org/2000/svg" width="17.5" height="19" viewBox="0 0 33 40" aria-hidden="true" focusable="false"><path fill="currentColor" d="M0 0h13l20 20-20 20H0l20-20z"></path></svg>'

/**
 * Shows an interactive tour of the application
 */
export default class OnboardingTour extends HTMLElement {
  /** @type {ReturnType<typeof introJs.tour> | null} */
  #tour = null
  /** @type {HTMLElement | null} */
  #tourContainer = null
  /** @type {MutationObserver | null} */
  #tooltipObserver = null
  #skipFullscreen = false
  #onboardingTourPage = this.getAttribute('onboarding-tour-page')
  #onBoardingLocalStorageKey = 'hide-onboarding-tour-' + this.#onboardingTourPage

  async connectedCallback () {
    if (!window.location.search.includes('showOnboardingTour=true') && window.localStorage.getItem(this.#onBoardingLocalStorageKey) === 'true') {
      return
    }
    window.localStorage.removeItem(this.#onBoardingLocalStorageKey)
    this.#tour = introJs.tour(TOUR_CONTAINER_MAP[this.#onboardingTourPage])
    this.#configureTour()
    await this.#tour.start()
    this.#tourContainer = document.querySelector('.introjs-tour')
    this.#updateDOMAfterRerender()
    this.#bindFocusTrapAndSkipFullscreenListener()
  }

  #configureTour () {
    if (!this.#tour) return
    const steps = TOUR_STEPS_MAP[this.#onboardingTourPage]
    this.#tour.setOptions({
      showBullets: false,
      hidePrev: true,
      steps
    })

    this.#tour.onBeforeChange(async (targetElement, targetIndex, direction) => {
      // If click the "done" button, there won't be a targetElement.
      if (!targetElement || !this.#tour) return true

      const targetStep = this.#tour.getStep(targetIndex)
      // When the user clicks the next button, check if the next step is available and if not, skip to the next available step.
      if (!this.isStepAvailable(targetStep.title)) {
        const skipTo = this.#findNextAvailableStep(targetIndex, direction)
        if (skipTo === -1) {
          await this.#tour.exit()
          return false
        }
        const adjust = direction === 'forward' ? 1 : -1
        this.#tour.setCurrentStep(skipTo + adjust)
        this.#runStepActions(this.#tour.getStep(skipTo).title)
        return false
      }

      this.#runStepActions(targetStep.title)
      return true
    })

    this.#tour.onExit(() => {
      window.localStorage.setItem(this.#onBoardingLocalStorageKey, 'true')
      this.#cleanup()
    })
  }

  #cleanup () {
    const tour = this.#tour
    this.#tour = null
    this.#tourContainer = null
    this.#tooltipObserver?.disconnect()
    this.#tooltipObserver = null

    // Remove the showOnboardingTour=true from the URL
    const url = new URL(window.location.href)
    url.searchParams.delete('showOnboardingTour')
    window.history.replaceState(null, '', url.pathname + url.search)
    if (document.fullscreenElement) {
      document.exitFullscreen()
    }

    // set focus to top of the page after exit
    window.scrollTo(0, 0)
    document.body.setAttribute('tabindex', '-1')
    document.body.focus()
    document.body.removeAttribute('tabindex')

    tour?.exit()
  }

  disconnectedCallback () {
    this.#cleanup()
  }

  /**
   * @param {string} tourTitle
   */
  #runStepActions (tourTitle) {
    const stepActions = STEP_ACTIONS_MAP[this.#onboardingTourPage]
    if (!stepActions) return

    // set context for step actions in order to keep private state isolated to the tour instance
    /** @type {import('./onboarding-config').StepActionContext} */
    const context = {
      clickTab: (selector) => this.clickTab(selector),
      openSummary: (selector) => this.openSummary(selector),
      getSkipFullscreen: () => this.#skipFullscreen,
      setSkipFullscreen: (value) => { this.#skipFullscreen = value }
    }
    stepActions(context, tourTitle)
  }

  /**
   * @param {number} fromIndex
   * @param {'forward' | 'backward'} direction
   * @returns {number} The index of the next available step or -1 if no next step is available
   */
  #findNextAvailableStep (fromIndex, direction) {
    if (!this.#tour) return -1
    const steps = this.#tour.getSteps()
    const isForward = direction === 'forward'
    let i = isForward ? fromIndex + 1 : fromIndex - 1

    while (i >= 0 && i < steps.length) {
      if (this.isStepAvailable(steps[i].title)) return i
      i = isForward ? i + 1 : i - 1
    }
    return -1
  }

  /**
   * @param {string} title
   * @returns {boolean} True if the step is available, false otherwise
   */
  isStepAvailable (title) {
    const isStepAvailable = IS_STEP_AVAILABLE_MAP[this.#onboardingTourPage]
    return isStepAvailable?.(title) ?? true
  }

  /** @param {string} parentSelector */
  openSummary (parentSelector) {
    /** @type {HTMLDetailsElement | null} */
    const details = document.querySelector(parentSelector + ' details')
    if (!details || details.open) return
    details.querySelector('summary')?.click()
    details.open = true // TODO: investigate why this is needed to ensure the summary is expanded on tour
  }

  /**
   * @param {string} parentSelector
   */
  clickTab (parentSelector) {
    /** @type {HTMLElement | null} */
    const tab = document.querySelector(parentSelector)
    tab?.click()
  }

  /**
   * Accessibility fix - keep focus within the modal
   * intro.js replaces tooltip controls each step; using the stable root to find the new elements.
   */
  #bindFocusTrapAndSkipFullscreenListener () {
    if (!this.#tourContainer) return

    this.#tourContainer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') {
        return
      }

      const target = /** @type {HTMLElement} */ (e.target)

      if (target?.classList.contains('introjs-skipbutton') || target?.classList.contains('introjs-tooltip')) {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault()
          /** @type {HTMLElement | null | undefined} */
          const skipFullscreenButton = this.#tourContainer?.querySelector('#skip-fullscreen')
          /** @type {HTMLElement | null | undefined} */
          const next = this.#tourContainer?.querySelector('.introjs-nextbutton')
          if (skipFullscreenButton) {
            skipFullscreenButton.focus()
          } else {
            next?.focus()
          }
        }
        return
      }

      if (target?.classList.contains('introjs-nextbutton')) {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault()
          /** @type {HTMLElement | null | undefined} */
          const skipFullscreenButton = this.#tourContainer?.querySelector('#skip-fullscreen')
          /** @type {HTMLElement | null | undefined} */
          const skipButton = this.#tourContainer?.querySelector('.introjs-skipbutton')

          if (skipFullscreenButton) {
            skipFullscreenButton.focus()
          } else {
            skipButton?.focus()
          }
        }
      }

      if (target?.id === 'skip-fullscreen') {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault()
          /** @type {HTMLElement | null | undefined} */
          const skipButton = this.#tourContainer?.querySelector('.introjs-skipbutton')
          skipButton?.focus()
        }
      }
    })

    // Detect skip-fullscreen via click event.
    // note: Intro.js's keyboard navigation calls preventDefault() on the Enter so have extra check in runStepActions()
    this.#tourContainer.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target)
      if (target?.id === 'skip-fullscreen') {
        this.#skipFullscreen = true
        this.#tour?.nextStep()
      }
    })
  }

  /**
   * Updates Aria labels and the text for the next button and the "Continue without fullscreen" button on step 1
   * Also adds the "Continue without fullscreen" button to the button row. This was due to styles to have it below the next button in order to definitely be secondary.
   * (intro.js re-renders introjs-tooltip after onAfterChange actions wo we observe the stable tour root instead.)
   */
  #updateDOMAfterRerender () {
    if (!this.#tourContainer || this.#tooltipObserver) return

    this.#tooltipObserver = new MutationObserver(() => {
      /** @type {HTMLDialogElement | null} */
      const dialog = document.querySelector('.introjs-tooltip')
      /** @type {HTMLButtonElement | null} */
      const skipButton = document.querySelector('.introjs-skipbutton')
      /** @type {HTMLAnchorElement | null} */
      const nextButton = document.querySelector('.introjs-nextbutton')
      /** @type {HTMLDivElement | null} */
      const step1Tooltip = document.querySelector('.onboarding-tour--intro')

      if (dialog && dialog.getAttribute('aria-label') !== 'Interactive demo') {
        dialog.setAttribute('aria-label', 'Interactive demo')
      }

      if (dialog && !dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1')
        // need to wait to act after intro.js focusses on the next button
        setTimeout(() => {
          dialog.focus()
        }, 10)
      }

      if (skipButton && skipButton.getAttribute('aria-label') !== 'Close the interactive demo') {
        skipButton.setAttribute('aria-label', 'Close the interactive demo')
      }

      // On step 1, customise the Next button label/icon and add a sibling
      // "Continue without fullscreen" button to the button row.
      if (step1Tooltip && nextButton && !nextButton.dataset.customised) {
        nextButton.dataset.customised = 'true'

        nextButton.textContent = 'Start tour'
        nextButton.insertAdjacentHTML('beforeend', START_ICON_SVG)

        if (this.#onboardingTourPage !== 'extraction') {
          return
        }

        const buttonRow = step1Tooltip.querySelector('.introjs-tooltipbuttons')
        if (buttonRow && !document.querySelector('#skip-fullscreen')) {
          const skipFullscreenButton = document.createElement('button')
          skipFullscreenButton.classList.add('govuk-button', 'govuk-button--secondary', 'onboarding-tour__skip-fullscreen-button')
          skipFullscreenButton.textContent = 'Continue without fullscreen'
          skipFullscreenButton.id = 'skip-fullscreen'
          skipFullscreenButton.type = 'button'
          skipFullscreenButton.tabIndex = 0
          buttonRow.appendChild(skipFullscreenButton)
        }
      }
    })
    this.#tooltipObserver.observe(this.#tourContainer, {
      childList: true,
      subtree: true
    })
  }
}

customElements.define('onboarding-tour', OnboardingTour)
